import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { formatPrice } from '../utils/astropayService';
import {
  ArrowLeft, FileText, ExternalLink, Calendar, CheckCircle2,
  Clock, XCircle, Receipt, Download,
} from 'lucide-react';

/**
 * /sistema/historial-pagos
 * --------------------------
 * Vista para el dueño con sus pagos:
 *   · subscriptions (procesadas por MP / AstroPay)
 *   · payment_receipts (comprobantes manuales)
 * Permite descargar / re-abrir links de pago + ver eventos del payment_events.
 */
export default function HistorialPagos() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const orgId = profile?.organization_id;

  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [events, setEvents] = useState([]);

  const fetchData = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [subRes, recRes, evRes] = await Promise.all([
        supabase.from('subscriptions').select('*').eq('organization_id', orgId).order('created_at', { ascending: false }),
        supabase.from('payment_receipts').select('*').eq('organization_id', orgId).order('created_at', { ascending: false }),
        supabase.from('payment_events').select('event_type, created_at, payload, error_message, provider').eq('organization_id', orgId).order('created_at', { ascending: false }).limit(50),
      ]);
      setSubscriptions(subRes.data || []);
      setReceipts(recRes.data || []);
      setEvents(evRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [orgId]);

  const statusStyle = (status) => {
    switch (status) {
      case 'paid':
      case 'approved':
        return 'bg-[var(--color-accent-soft)] text-[var(--color-accent-dark)] border border-[color:var(--color-accent)]';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'cancelled':
      case 'rejected':
        return 'bg-red-50 text-red-700 border border-red-200';
      default:
        return 'bg-[var(--surface-2)] text-[var(--text-muted)] border border-[var(--gst-border)]';
    }
  };

  const statusLabel = (status) => ({
    paid: 'Pagado',
    approved: 'Aprobado',
    pending: 'Pendiente',
    cancelled: 'Cancelado',
    rejected: 'Rechazado',
  }[status] || status);

  return (
    <div className="min-h-screen bg-[#FBF8F1] p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#3F3F46] text-[12px] font-semibold mb-4 transition-all"
        >
          <ArrowLeft size={14} /> Volver
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-lg bg-[var(--gst-mint-100)] flex items-center justify-center">
            <Receipt size={22} className="text-[var(--gst-mint-700)]" />
          </div>
          <div>
            <h1 className="text-[22px] font-black text-[#3F3F46]">Historial de pagos</h1>
            <p className="text-[12px] text-[#A1A1AA]">
              Suscripciones, comprobantes manuales y eventos de pago de tu organización
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-[12px] text-[#A1A1AA]">Cargando...</div>
        ) : (
          <>
            {/* Suscripciones */}
            <section className="bg-[var(--surface)] rounded-xl border border-[#E4E4E7] shadow-sm overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#F4F4F5]">
                <h2 className="text-[12px] font-black uppercase tracking-widest text-[#A1A1AA] flex items-center gap-2">
                  <Calendar size={14} /> Suscripciones ({subscriptions.length})
                </h2>
              </div>
              {subscriptions.length === 0 ? (
                <div className="py-12 text-center text-[12px] text-[#A1A1AA]">
                  Todavía no hay suscripciones generadas.
                </div>
              ) : (
                <div className="divide-y divide-[#F4F4F5]">
                  {subscriptions.map((sub) => (
                    <div key={sub.id} className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-3 hover:bg-[#FAFAFA] transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        {sub.status === 'paid' ? <CheckCircle2 size={16} className="text-[var(--gst-mint-600)]" />
                          : sub.status === 'pending' ? <Clock size={16} className="text-amber-500" />
                          : <XCircle size={16} className="text-red-500" />}
                        <div>
                          <p className="text-[13px] font-bold text-[#3F3F46]">
                            {sub.plan_name || sub.plan_key}
                          </p>
                          <p className="text-[10px] text-[#A1A1AA]">
                            {sub.period_start} → {sub.period_end} · {new Date(sub.created_at).toLocaleDateString('es-AR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-[13px] font-black text-[#3F3F46]">{formatPrice(sub.amount)}</span>
                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${statusStyle(sub.status)}`}>
                          {statusLabel(sub.status)}
                        </span>
                        {sub.payment_url && sub.status === 'pending' && (
                          <a
                            href={sub.payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[10px] font-bold text-[var(--gst-mint-700)] hover:underline"
                          >
                            Pagar <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Comprobantes manuales */}
            <section className="bg-[var(--surface)] rounded-xl border border-[#E4E4E7] shadow-sm overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-[#F4F4F5]">
                <h2 className="text-[12px] font-black uppercase tracking-widest text-[#A1A1AA] flex items-center gap-2">
                  <FileText size={14} /> Comprobantes manuales ({receipts.length})
                </h2>
              </div>
              {receipts.length === 0 ? (
                <div className="py-12 text-center text-[12px] text-[#A1A1AA]">
                  No subiste comprobantes de transferencia.
                </div>
              ) : (
                <div className="divide-y divide-[#F4F4F5]">
                  {receipts.map((rec) => (
                    <div key={rec.id} className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-3 hover:bg-[#FAFAFA]">
                      <div>
                        <p className="text-[13px] font-bold text-[#3F3F46]">
                          {rec.bank_name || 'Transferencia'} {rec.reference_code ? `· ${rec.reference_code}` : ''}
                        </p>
                        <p className="text-[10px] text-[#A1A1AA]">
                          {rec.payment_date || rec.created_at?.slice(0, 10)} · {formatPrice(rec.amount)}
                          {rec.rejection_reason ? ` · Motivo: ${rec.rejection_reason}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${statusStyle(rec.status)}`}>
                          {statusLabel(rec.status)}
                        </span>
                        {rec.receipt_url && !rec.receipt_url.startsWith('pending-upload:') && (
                          <a
                            href={rec.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-[10px] font-bold text-[var(--gst-mint-700)] hover:underline"
                          >
                            <Download size={10} /> Ver
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Eventos */}
            {events.length > 0 && (
              <section className="bg-[var(--surface)] rounded-xl border border-[#E4E4E7] shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-[#F4F4F5]">
                  <h2 className="text-[12px] font-black uppercase tracking-widest text-[#A1A1AA]">
                    Eventos recientes ({events.length})
                  </h2>
                </div>
                <div className="divide-y divide-[#F4F4F5] max-h-96 overflow-y-auto">
                  {events.map((ev, idx) => (
                    <div key={idx} className="px-5 py-2.5 flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-[#3F3F46]">{ev.event_type}</span>
                        {ev.provider && <span className="text-[#A1A1AA]">({ev.provider})</span>}
                      </div>
                      <span className="text-[#A1A1AA]">{new Date(ev.created_at).toLocaleString('es-AR')}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
